import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as gamesApi from '../api/games';

export const useGames = (params = {}) => {
  return useQuery({
    queryKey: ['games', params],
    queryFn: () => gamesApi.getGames(params),
  });
};

export const useGame = (id) => {
  return useQuery({
    queryKey: ['game', id],
    queryFn: () => gamesApi.getGame(id),
    enabled: !!id,
  });
};

export const useCreateGame = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: gamesApi.createGame,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['games'] });
    },
  });
};

export const useUpdateGame = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => gamesApi.updateGame(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['games'] });
      queryClient.invalidateQueries({ queryKey: ['game', variables.id] });
    },
  });
};

export const useDeleteGame = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: gamesApi.deleteGame,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['games'] });
    },
  });
};

export const useJoinGame = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, inviteCode }) => gamesApi.joinGame(id, inviteCode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['games'] });
    },
  });
};

export const useLeaveGame = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: gamesApi.leaveGame,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['games'] });
    },
  });
};

/**
 * Combined game state hook - fetches all game data in one request
 * Replaces: useGame, useCurrentRound, useScenes, useCharacters,
 * useMyBalance, useGameBalances, useDetails, useReadyStatus, useContributionStatus
 */
export const useGameState = (id) => {
  return useQuery({
    queryKey: ['gameState', id],
    queryFn: () => gamesApi.getGameState(id),
    enabled: !!id,
    refetchInterval: 30000, // Refresh every 30 seconds
  });
};
